import { Api } from "../../../../Api";
import { DataFrame, Method, Status } from "../../../../DataFrame";
import { DependencyContainer } from "../../../../DependencyContainer";
import { ControllerExtensionsService } from "../../../Controller/ControllerExtensionsService";
import { IControllerExtensionsService } from "../../../Controller/IControllerExtensionsService";
import { ExalusConnectionService } from "../../../ExalusConnectionService";
import { ResponseResult } from "../../../FieldChangeResult";
import { IExalusConnectionService } from "../../../IExalusConnectionService";
import { ILoggerService } from "../../../Logging/ILoggerService";
import { LoggerService } from "../../../Logging/LoggerService";
import { ChatService, ChatServiceErrorCode } from "../../ChatService";
import { IMessage, MessageHandler } from "../../IChatService";
import { ChatGptClientError, ChatGptConfiguration, ControllerActivity, IGptChat, IGptChatMessage } from "./IGptChat";

export class GptChat implements IGptChat {

    private _connection: IExalusConnectionService;
    private _chatSubscriptions: MessageHandler<any>[] = [];

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        let removeSubCallback = this._connection.SubscribeTo<GptChatResponse>("/homemessaging/gpt/notify/message/new", (frame) => {
            const data = frame.Data as GptChatResponse;

            const res = new GptChatMessage();
            res.DateTime = data.DateTime;
            res.Guid = data.Guid;
            res.MessageText = data.MessageText ? data.MessageText : "";
            res.SenderName = data.SenderName ? data.SenderName : "n/a";

            if (data.ControllerActivities != null) {
                for (let [activity, activityDictionaryParams] of Object.entries(data.ControllerActivities)) {
                    const activityKey = ControllerActivity[activity as keyof typeof ControllerActivity];
                    const activityParamMap = new Map<string, string>();

                    for (let [paramKey, paramVal] of Object.entries(activityDictionaryParams as any)) {
                        activityParamMap.set(paramKey, paramVal as string);
                    }

                    res.ControllerActivities.set(activityKey, activityParamMap);
                }
            }

            if (data.Errors != null) {
                for (let [k, v] of Object.entries(data.Errors)) {
                    res.Errors.set(ChatGptClientError[k as keyof typeof ChatGptClientError], v as string);
                }
            }

            this._chatSubscriptions.forEach(handler => {
                handler(res);
            })
        });
    }

    async IsGptChatAvailableAsync(): Promise<boolean> {
        return (await Api.Get<IControllerExtensionsService>(ControllerExtensionsService.ServiceName).GetExtensionsInfoAsync()).any(e => e.ExtensionGuid == "80daeab9-104f-4c1b-9464-61f5fc9bf2bf");
    }

    async GetChatConfigurationAsync(): Promise<ChatGptConfiguration | ResponseResult<ChatServiceErrorCode>> {
        try {
            const result = await this._connection.SendAndWaitForResponseAsync<ChatGptConfiguration>(new GetConfigurationRequest(), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<ChatServiceErrorCode>(ChatServiceErrorCode.FatalError, `Cannot get chat configuration! No data in result`);

            if (result.Status != Status.OK)
                return new ResponseResult<ChatServiceErrorCode>(ChatServiceErrorCode.Error, `Cannot get chat configuration! Controller responded with code: ${result.Status}`);

            if (result.Data == null)
                return new ResponseResult<ChatServiceErrorCode>(ChatServiceErrorCode.NoData, `Cannot get chat configuration! Controller responded with success, but no data in response!`);

            return result.Data;

        } catch (error) {
            return new ResponseResult<ChatServiceErrorCode>(ChatServiceErrorCode.FatalError, `Cannot get chat configuration! ${error}`);
        }
    }

    async SetChatConfigurationAsync(config: ChatGptConfiguration): Promise<Status> {
        try {
            const result = await this._connection.SendAndWaitForResponseAsync<any>(new SetConfigurationRequest(config), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;

        } catch (error) {
            DependencyContainer.Log?.Error(ChatService.ServiceName, `Cannot set chat configuration! ${error}`);
            return Status.FatalError;
        }
    }

    async SendMessageAsync(message: string): Promise<Status> {
        try {
            const result = await this._connection.SendAndWaitForResponseAsync<string>(new SendMessagesRequest(message), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;

        } catch (error) {
            DependencyContainer.Log?.Error(ChatService.ServiceName, `Cannot send message! ${error}`);
            return Status.FatalError;
        }
    }

    SubscribeToMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        this._chatSubscriptions?.push(messageHandler);
    }

    UnsubscribeFromMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        if (this._chatSubscriptions)
            this._chatSubscriptions = this._chatSubscriptions.filter(h => h !== messageHandler);
    }
}

class SendMessagesRequest extends DataFrame<string> {
    constructor(message: string) {
        super();
        this.Resource = "/homemessaging/gpt/message";
        this.Method = Method.Put;
        this.Data = message;
    }
}

class GetConfigurationRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/homemessaging/gpt/configuration";
        this.Method = Method.Get;
    }
}

class SetConfigurationRequest extends DataFrame<ChatGptConfiguration> {
    constructor(data: ChatGptConfiguration) {
        super();
        this.Resource = "/homemessaging/gpt/configuration";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class GptChatResponse {
    Guid: string = "";
    MessageText: string = "";
    SenderName: string = "";
    DateTime: string = new Date().toDateString();
    ControllerActivities: any;
    Errors: any;
}

class GptChatMessage implements IGptChatMessage {

    private _messageActions: Map<ControllerActivity, Map<string, string>> = new Map();
    private _errors: Map<ChatGptClientError, string> = new Map();
    private _guid: string = "";
    private _senderName: string = "";
    private _messageText: string = "";
    private _dateTime: string = "";

    get ControllerActivities(): Map<ControllerActivity, Map<string, string>> {
        return this._messageActions;
    }
    get Errors(): Map<ChatGptClientError, string> {
        return this._errors;
    }
    get Guid(): string {
        return this._guid;
    }
    get SenderName(): string {
        return this._senderName;
    }
    get MessageText(): string {
        return this._messageText;
    }
    get DateTime(): string {
        return this._dateTime;
    }

    set ControllerActivities(value: Map<ControllerActivity, Map<string, string>>) {
        this._messageActions = value;
    }
    set Errors(value: Map<ChatGptClientError, string>) {
        this._errors = value;
    }
    set Guid(value: string) {
        this._guid = value;
    }
    set SenderName(value: string) {
        this._senderName = value;
    }
    set MessageText(value: string) {
        this._messageText = value;
    }
    set DateTime(value: string) {
        this._dateTime = value;
    }
}